import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { api, refreshToken } from "../services/api";
import { LoginData, RegisterData, Token, User } from '../types/types';

interface AuthContextType {
  user: User | null;
  token: Token;
  createAccount: (createAccountData: RegisterData) => Promise<void>;
  signIn: (loginData: LoginData) => Promise<void>;
  signOut: () => void;
  updateUser: (userData: Partial<User>) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>(
  {
    user: null,
    token: { accessToken: '', refreshToken: '' },
    createAccount: async () => { },
    signIn: async () => { },
    signOut: () => { },
    updateUser: async () => { },
  } as AuthContextType
);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<Token>({ accessToken: '', refreshToken: '' });
  const [tokenRefreshed, setTokenRefreshed] = useState(false);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(JSON.parse(storedToken));
    }
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    } else {
      setUser(null)
    }
    if (storedToken && storedUser) {
      handleRefreshToken();
    }
  }, []);

  const createAccount = async (createAccountData: RegisterData) => {
    let success = false;
    try {
      const response = await api.post('/auth/signup', createAccountData);
      success = true;
    } catch (error) {
      console.error('Authentication error:', error);
      alert('Erro ao criar conta')
    }
    if (success) {
      try {
        await signIn({ identifier: createAccountData.email, password: createAccountData.password })
      } catch (error) {
        console.error('Authentication error:', error);
        alert('Erro ao criar conta')
      }
    }
    //temp
    if (success) {
      await signIn({ identifier: createAccountData.email, password: createAccountData.password })
    }
  }

  const signIn = async (loginData: LoginData) => {
    try {
      const response = await api.post('/auth/login', loginData);
      // console.log('Login response:', response);
      const accessToken = response.data.access_token;
      const refreshToken = response.data.refresh_token;
      const username = response.data.username;
      const profileResponse = await api.get(`/profile/${username}`);
      setToken({ accessToken: accessToken, refreshToken: refreshToken });
      localStorage.setItem('token', JSON.stringify({ accessToken: accessToken, refreshToken: refreshToken }));
      setUser({
        id: profileResponse.data.id,
        userId: profileResponse.data.userId,
        username: profileResponse.data.username,
        avatarUrl: profileResponse.data.avatarUrl,
        coverUrl: profileResponse.data.coverUrl,
        followersNumber: profileResponse.data.followersNumber,
        followingNumber: profileResponse.data.followingNumber,
        displayName: profileResponse.data.displayName,
        birthDate: profileResponse.data.birthDate,
        bio: profileResponse.data.bio,
        createdAt: profileResponse.data.createdAt,
        updatedAt: profileResponse.data.updatedAt,
      });
      localStorage.setItem('user', JSON.stringify({
        id: profileResponse.data.id,
        userId: profileResponse.data.userId,
        username: profileResponse.data.username,
        avatarUrl: profileResponse.data.avatarUrl,
        coverUrl: profileResponse.data.coverUrl,
        followersNumber: profileResponse.data.followersNumber,
        followingNumber: profileResponse.data.followingNumber,
        displayName: profileResponse.data.displayName,
        birthDate: profileResponse.data.birthDate,
        bio: profileResponse.data.bio,
        createdAt: profileResponse.data.createdAt,
        updatedAt: profileResponse.data.updatedAt,
      }));
      setTokenRefreshed(true);
    } catch (error) {
      alert('Usuário ou senha inválidos')
      console.error('Authentication error:', error);
      // throw error;
    }
  };

  const signOut = () => {
    // console.log('Signing out');
    // console.log('Token:', token);
    try {
      api.post('/auth/logout', {
        headers: {
          'Authorization': `Bearer ${token.accessToken}`
        }
      });
      setUser(null);
      setToken({ accessToken: '', refreshToken: '' });
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const updateUser = async (userData: Partial<User>) => {
    try {
      const response = await api.put(`/users/${user?.id}`, userData);
      setUser(prevUser => ({
        ...prevUser!,
        ...userData
      }));
      localStorage.setItem('user', JSON.stringify({ ...user, ...userData }));
    } catch (error) {
      console.error('Update user error:', error);
    }
  };

  const handleRefreshToken = async () => {
    if (token.accessToken === '' || token.refreshToken === '') {
      return;
    }
    try {
      const data = await refreshToken(token.refreshToken);
      if (!data) {
        signOut();
        return;
      }
      console.log('Refresh token data:', data);
      setTokenRefreshed(true);
      setToken(prev => ({
        ...prev,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      }));
      localStorage.setItem('token', JSON.stringify({ accessToken: data.accessToken, refreshToken: data.refreshToken }));
    } catch (error) {
      console.error('Failed to refresh token', error);
    }
  }

  useEffect(() => {
    if (token && token.accessToken !== '' && token.refreshToken !== '' && tokenRefreshed === false) {
      handleRefreshToken();
    } else {
      return;
    }
    const interval = setInterval(() => {
      setTokenRefreshed(false);
    }, 1000 * 60 * 30);
    return () => clearInterval(interval);
  }, [token, tokenRefreshed]);

  return (
    <AuthContext.Provider
      value={{ user, token, createAccount, signIn, signOut, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};